import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Layout,
  Columns,
  Type,
  Sidebar,
  Cloud,
  MessageSquare,
  Timer,
  Newspaper,
  Clock,
  Plus,
  Search,
  Copy,
  Trash2,
  Eye,
} from 'lucide-react';

const TEMPLATE_ICONS = {
  'full-screen': Layout,
  'split-screen': Columns,
  'ticker': Type,
  'l-bar': Sidebar,
  'weather': Cloud,
  'social': MessageSquare,
  'countdown': Timer,
  'news': Newspaper,
  'clock': Clock,
};

const DEFAULT_TEMPLATES = [
  {
    id: 'default-fullscreen',
    name: 'Full Screen Display',
    type: 'basic',
    category: 'full-screen',
    description: 'Single content fills entire screen',
    preview_url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400',
    layout: { zones: [{ id: 'main', x: 0, y: 0, width: 100, height: 100 }] },
    is_public: true,
    created_by: 'system',
    created_at: '2026-01-01T00:00:00Z'
  },
  {
    id: 'default-split',
    name: 'Split Screen',
    type: 'basic',
    category: 'split-screen',
    description: 'Two content areas side by side',
    preview_url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    layout: { zones: [{ id: 'left', x: 0, y: 0, width: 50, height: 100 }, { id: 'right', x: 50, y: 0, width: 50, height: 100 }] },
    is_public: true,
    created_by: 'system',
    created_at: '2026-01-01T00:00:00Z'
  },
  {
    id: 'default-ticker',
    name: 'Bottom Ticker',
    type: 'basic',
    category: 'ticker',
    description: 'Scrolling text bar at bottom',
    preview_url: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400',
    layout: { zones: [{ id: 'main', x: 0, y: 0, width: 100, height: 85 }, { id: 'ticker', x: 0, y: 85, width: 100, height: 15 }] },
    is_public: true,
    created_by: 'system',
    created_at: '2026-01-01T00:00:00Z'
  },
  {
    id: 'default-lbar',
    name: 'L-Bar Layout',
    type: 'basic',
    category: 'l-bar',
    description: 'Content with sidebar and bottom bar',
    preview_url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400',
    layout: { zones: [{ id: 'main', x: 0, y: 0, width: 75, height: 85 }, { id: 'sidebar', x: 75, y: 0, width: 25, height: 100 }, { id: 'bottom', x: 0, y: 85, width: 75, height: 15 }] },
    is_public: true,
    created_by: 'system',
    created_at: '2026-01-01T00:00:00Z'
  },
  {
    id: 'default-weather',
    name: 'Weather Widget',
    type: 'dynamic',
    category: 'weather',
    description: 'Live weather data display',
    preview_url: 'https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=400',
    layout: { widget: 'weather', location: 'auto', units: 'imperial' },
    is_public: true,
    created_by: 'system',
    created_at: '2026-01-01T00:00:00Z'
  },
  {
    id: 'default-countdown',
    name: 'Event Countdown',
    type: 'dynamic',
    category: 'countdown',
    description: 'Countdown timer to an event',
    preview_url: 'https://images.unsplash.com/photo-1533749871411-5e21e14bcc7d?w=400',
    layout: { widget: 'countdown', target_date: null, message: 'Coming Soon' },
    is_public: true,
    created_by: 'system',
    created_at: '2026-01-01T00:00:00Z'
  },
];

export default function TemplatesPage() {
  const { user, isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({ basic: [], dynamic: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'basic',
    category: 'full-screen',
    description: '',
    preview_url: '',
    is_public: true,
    layout: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        api.get('/templates'),
        api.get('/templates/categories'),
      ]);
      // Combine API templates with defaults
      const apiTemplates = templatesRes.data || [];
      const allTemplates = [...DEFAULT_TEMPLATES, ...apiTemplates.filter(t => !t.id.startsWith('default-'))];
      setTemplates(allTemplates);
      setCategories(categoriesRes.data);
    } catch (error) {
      setTemplates(DEFAULT_TEMPLATES);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/templates', formData);
      toast.success('Template created successfully');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleUseTemplate = (template) => {
    // Copy template to clipboard or navigate to content creation
    toast.success(`Template "${template.name}" ready to use`);
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || t.type === activeTab;
    return matchesSearch && matchesTab;
  });

  const getCategoryIcon = (category) => {
    const IconComponent = TEMPLATE_ICONS[category] || Layout;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Content Templates
          </h1>
          <p className="text-zinc-400 mt-1">Pre-built layouts for quick content creation</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 btn-glow"
            data-testid="create-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="all" className="data-[state=active]:bg-blue-600">All</TabsTrigger>
            <TabsTrigger value="basic" className="data-[state=active]:bg-blue-600">Basic</TabsTrigger>
            <TabsTrigger value="dynamic" className="data-[state=active]:bg-blue-600">Dynamic</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredTemplates.map((template) => {
          const IconComponent = getCategoryIcon(template.category);
          return (
            <Card key={template.id} className="glass-card border-zinc-800 card-hover overflow-hidden">
              {/* Preview Image */}
              <div className="relative h-40 bg-zinc-900">
                {template.preview_url ? (
                  <img
                    src={template.preview_url}
                    alt={template.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <IconComponent className="w-16 h-16 text-zinc-700" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Badge
                    variant="outline"
                    className={template.type === 'dynamic' 
                      ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' 
                      : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                    }
                  >
                    {template.type}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{template.name}</h3>
                    <p className="text-zinc-500 text-sm truncate">{template.description}</p>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewTemplate(template)}
                    className="flex-1 border-zinc-700"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleUseTemplate(template)}
                    className="flex-1 bg-blue-600 hover:bg-blue-500"
                    data-testid={`use-template-${template.id}`}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Use
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <Layout className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No templates found</h3>
          <p className="text-zinc-400">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Create Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Create Template
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTemplate} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="dynamic">Dynamic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {formData.type === 'basic' ? (
                      <>
                        <SelectItem value="full-screen">Full Screen</SelectItem>
                        <SelectItem value="split-screen">Split Screen</SelectItem>
                        <SelectItem value="ticker">Ticker</SelectItem>
                        <SelectItem value="l-bar">L-Bar</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="weather">Weather</SelectItem>
                        <SelectItem value="social">Social Feed</SelectItem>
                        <SelectItem value="countdown">Countdown</SelectItem>
                        <SelectItem value="news">News</SelectItem>
                        <SelectItem value="clock">Clock</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Preview Image URL</Label>
              <Input
                value={formData.preview_url}
                onChange={(e) => setFormData({ ...formData, preview_url: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="https://..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                Create Template
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Layout Preview - Visual wireframe of zones */}
            <div className="relative w-full h-64 bg-zinc-950 rounded-lg border border-zinc-800 overflow-hidden">
              {previewTemplate?.layout?.zones ? (
                // Render zone layout for basic templates
                previewTemplate.layout.zones.map((zone, index) => (
                  <div
                    key={zone.id}
                    className="absolute border-2 border-dashed border-blue-500/50 bg-blue-500/10 flex items-center justify-center transition-all"
                    style={{
                      left: `${zone.x}%`,
                      top: `${zone.y}%`,
                      width: `${zone.width}%`,
                      height: `${zone.height}%`,
                    }}
                  >
                    <div className="text-center">
                      <div className="w-8 h-8 mx-auto mb-1 rounded bg-blue-500/20 flex items-center justify-center">
                        <Layout className="w-4 h-4 text-blue-400" />
                      </div>
                      <span className="text-blue-400 text-xs font-medium uppercase">{zone.id}</span>
                    </div>
                  </div>
                ))
              ) : previewTemplate?.layout?.widget ? (
                // Render widget preview for dynamic templates
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    {(() => {
                      const IconComponent = TEMPLATE_ICONS[previewTemplate.category] || Layout;
                      return <IconComponent className="w-16 h-16 text-purple-400 mx-auto mb-2" />;
                    })()}
                    <p className="text-purple-400 font-medium">{previewTemplate.layout.widget} Widget</p>
                    <p className="text-zinc-500 text-sm mt-1">Dynamic content will be loaded here</p>
                  </div>
                </div>
              ) : previewTemplate?.preview_url ? (
                // Fallback to image preview
                <img
                  src={previewTemplate.preview_url}
                  alt={previewTemplate.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                // Default placeholder
                <div className="w-full h-full flex items-center justify-center">
                  <Layout className="w-16 h-16 text-zinc-700" />
                </div>
              )}
              
              {/* Screen frame overlay */}
              <div className="absolute inset-0 border-4 border-zinc-700 rounded-lg pointer-events-none" />
              <div className="absolute top-2 left-2 bg-zinc-800 px-2 py-1 rounded text-xs text-zinc-400">
                1920 x 1080
              </div>
            </div>

            {/* Template info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm">Type</p>
                <p className="text-white font-medium capitalize">{previewTemplate?.type}</p>
              </div>
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm">Category</p>
                <p className="text-white font-medium capitalize">{previewTemplate?.category?.replace('-', ' ')}</p>
              </div>
            </div>
            
            {/* Zone details for basic templates */}
            {previewTemplate?.layout?.zones && (
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm mb-2">Layout Zones</p>
                <div className="flex flex-wrap gap-2">
                  {previewTemplate.layout.zones.map((zone) => (
                    <Badge 
                      key={zone.id} 
                      variant="outline" 
                      className="bg-blue-500/10 text-blue-400 border-blue-500/20"
                    >
                      {zone.id}: {zone.width}% × {zone.height}%
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Widget config for dynamic templates */}
            {previewTemplate?.layout?.widget && (
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-zinc-400 text-sm mb-2">Widget Configuration</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(previewTemplate.layout).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-zinc-500 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="text-white">{value === null ? 'Not set' : String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <p className="text-zinc-400">{previewTemplate?.description}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewTemplate(null)} className="border-zinc-700">
              Close
            </Button>
            <Button
              onClick={() => {
                handleUseTemplate(previewTemplate);
                setPreviewTemplate(null);
              }}
              className="bg-blue-600 hover:bg-blue-500"
            >
              Use Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
